import React, { useState, useEffect } from 'react';
import {DIVIDEND_PROJECTIONS, DIVIDEND_DISCLAIMER, CALCULATION_CONSTANTS, DYNAMIC_DENOMINATOR_NOTICE, SCALING_NOTICE, PLANT_ON_SITE_NOTICE, PLANT_ENGINEERING_LEAD_TIME_DAYS, computeDynamicAnnualProjection, projectionYearNeedsLiveShares, GOLD_KG_PER_MONTH_PER_PLANT} from '../../lib/services/calculationService';
import { useLiveTotalSharesSold } from '../../lib/hooks/useLiveTotalSharesSold';
import { DIVIDENDS_USER_VISIBLE } from '../../lib/dividendsUserVisible';
import { DividendCalculatorNotice } from './DividendCalculatorNotice';

interface DividendProjectionsProps {
  userShares?: number;
  variant?: 'full' | 'compact' | 'table';
  showSharesInput?: boolean;
}

export const DividendProjections2026to2035: React.FC<DividendProjectionsProps> = ({
  userShares = 1,
  variant = 'full',
  showSharesInput = true,
}) => {
  const [selectedYear, setSelectedYear] = useState<number>(2027);
  const [shares, setShares] = useState<number>(userShares);

  // Sync internal shares state with userShares prop when it changes
  // This ensures the calculator input controls the projections
  useEffect(() => {
    setShares(userShares);
  }, [userShares]);

  // Total shares constant for ownership calculation (single source of truth)
  const TOTAL_SHARES = CALCULATION_CONSTANTS.TOTAL_SHARES;

  // Live total shares sold. Profit is divided by this count, not by 1,400,000.
  const { totalSharesSold: liveSharesSold, loading: liveSharesLoading } = useLiveTotalSharesSold();
  const sharesReady = !liveSharesLoading && liveSharesSold > 0;
  const figureReady = (year: number) => !projectionYearNeedsLiveShares(year) || sharesReady;

  // 2026-2029: 1.4M-denominator phase (initial shareholders accumulating ROI)
  // 2030+:     3M-denominator phase (post-ROI expansion to fund 400+ plant roadmap)
  const years = [2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034, 2035, 2036];

  // Build a per-year projection that overlays the dynamic recompute (live
  // shares-sold denominator for Jan/Apr 2027) on top of the static
  // baseline. Display-only fields (kg/month, kg/year, isRampYear, monthsOperating,
  // note) come from the static projection unchanged.
  const getProjectionForYear = (year: number) => {
    const base = DIVIDEND_PROJECTIONS[year as keyof typeof DIVIDEND_PROJECTIONS];
    const dyn = computeDynamicAnnualProjection(year, liveSharesSold);
    if (!dyn) {
      return {
        ...base,
        estimatedDividendPerShare: 0,
        quarterlyPayouts: (base.quarterlyPayouts || []).map(p => ({ ...p, amount: 0 })),
        semiAnnualPayouts: (base.semiAnnualPayouts || []).map(p => ({ ...p, amount: 0 })),
      };
    }
    return {
      ...base,
      estimatedDividendPerShare: dyn.estimatedDividendPerShare,
      ...(dyn.quarterlyPayouts ? { quarterlyPayouts: dyn.quarterlyPayouts } : {}),
      ...(dyn.semiAnnualPayouts ? { semiAnnualPayouts: dyn.semiAnnualPayouts } : {}),
    };
  };

  const selectedData = getProjectionForYear(selectedYear);

  if (!DIVIDENDS_USER_VISIBLE) return null;

  const officialNotice = <DividendCalculatorNotice />;

  if (variant === 'compact') {
    return (
      <div style={{
        backgroundColor: 'rgba(31, 41, 55, 0.9)',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #374151'
      }}>
        {officialNotice}
        <h3 style={{ color: '#f59e0b', fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>
          📈 Dividend Projections (2026-2035)
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
          {years.map(year => {
            const data = getProjectionForYear(year);
            return (
              <div
                key={year}
                style={{
                  backgroundColor: year === 2026 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(55, 65, 81, 0.5)',
                  border: year === 2026 ? '2px solid #f59e0b' : '1px solid #374151',
                  borderRadius: '8px',
                  padding: '12px',
                  textAlign: 'center'
                }}
              >
                <div style={{ color: '#ffffff', fontSize: '14px', marginBottom: '4px' }}>{year}</div>
                <div style={{ color: '#10b981', fontSize: '20px', fontWeight: 'bold' }}>
                  {figureReady(year) ? `$${data.estimatedDividendPerShare}` : '—'}
                </div>
                <div style={{ color: '#ffffff', fontSize: '13px' }}>per share/year</div>
                {year === 2026 && (
                  <div style={{ color: '#f59e0b', fontSize: '13px', marginTop: '4px' }}>Setup Year</div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#d97706'
        }}>
          ⚠️ {DIVIDEND_DISCLAIMER}
        </div>
      </div>
    );
  }

  if (variant === 'table') {
    return (
      <div style={{
        backgroundColor: 'rgba(31, 41, 55, 0.9)',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #374151'
      }}>
        {officialNotice}
        <h3 style={{ color: '#f59e0b', fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>
          📊 Conservative Dividend Projections (2026-2035)
        </h3>
        <p style={{ color: '#ffffff', fontSize: '14px', marginBottom: '20px' }}>
          Dividends are directly tied to gold output — more gold mined = higher dividends
        </p>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #374151' }}>
                <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>YEAR</th>
                <th style={{ padding: '12px', textAlign: 'right', color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>GOLD CAPTURED (kg)</th>
                <th style={{ padding: '12px', textAlign: 'right', color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>DIVIDEND/SHARE</th>
                <th style={{ padding: '12px', textAlign: 'right', color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>YOUR DIVIDEND</th>
                <th style={{ padding: '12px', textAlign: 'left', color: '#ffffff', fontSize: '14px', fontWeight: '600' }}>NOTE</th>
              </tr>
            </thead>
            <tbody>
              {years.map(year => {
                const data = getProjectionForYear(year);
                const userDividend = data.estimatedDividendPerShare * shares;
                
                return (
                  <tr key={year} style={{ borderBottom: '1px solid #374151' }}>
                    <td style={{ padding: '16px', color: year === 2026 ? '#f59e0b' : '#fff', fontWeight: year === 2026 ? 'bold' : 'normal' }}>
                      {year}
                      {year === 2026 && <span style={{ marginLeft: '8px', fontSize: '13px', color: '#f59e0b' }}>⭐ Setup Year</span>}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#ffffff' }}>
                      {data.goldProductionKgPerYear.toLocaleString()}
                      {data.isRampYear && (
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', fontStyle: 'italic' }}>
                          {data.monthsOperating} operating months
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#10b981', fontWeight: 'bold' }}>
                      {figureReady(year) ? `$${data.estimatedDividendPerShare}` : '—'}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right', color: '#10b981', fontWeight: 'bold' }}>
                      {figureReady(year) ? `$${userDividend.toLocaleString()}` : '—'}
                    </td>
                    <td style={{ padding: '16px', color: '#ffffff', fontSize: '14px' }}>
                      {'note' in data ? data.note : '±'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{
          marginTop: '20px',
          padding: '16px',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#d97706'
        }}>
          <strong>⚠️ Important:</strong> {DIVIDEND_DISCLAIMER}
        </div>
      </div>
    );
  }

  // Full variant with detailed quarterly/semi-annual payouts
  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '16px',
      padding: '32px',
      border: '1px solid rgba(255, 193, 7, 0.2)'
    }}>
      {officialNotice}
      <h2 style={{ color: '#ffc107', fontSize: '28px', fontWeight: '700', marginBottom: '8px', textAlign: 'center' }}>
        📈 Dividend & Growth Projections (2026-2035)
      </h2>
      <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '15px', marginBottom: '16px', textAlign: 'center' }}>
        Conservative estimates based on projected gold yield • Dividends tied directly to gold output
      </p>

      <div style={{
        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(16, 185, 129, 0.12) 100%)',
        border: '2px solid #D4AF37',
        borderRadius: '12px',
        padding: '16px 18px',
        marginBottom: '28px',
        textAlign: 'center'
      }}>
        <div style={{ color: '#D4AF37', fontWeight: 800, fontSize: '15px', marginBottom: '6px' }}>
          1 × 100 TPH plant is on the property
        </div>
        <p style={{ color: '#fff', fontSize: '14px', lineHeight: 1.5, margin: 0 }}>
          {PLANT_ON_SITE_NOTICE}
        </p>
      </div>

      {/* Shares Input */}
      {showSharesInput && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '28px',
          padding: '20px',
          backgroundColor: 'rgba(255, 193, 7, 0.08)',
          border: '1px solid rgba(255, 193, 7, 0.3)',
          borderRadius: '12px'
        }}>
          <label style={{ color: '#ffc107', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            💎 Your Shares
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="number"
              value={shares}
              onChange={(e) => setShares(Math.max(1, parseInt(e.target.value) || 1))}
              min="1"
              max="100000"
              style={{
                width: '160px',
                padding: '14px 16px',
                fontSize: '22px',
                fontWeight: 'bold',
                textAlign: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '2px solid rgba(255, 193, 7, 0.5)',
                borderRadius: '12px',
                color: '#fff',
                outline: 'none'
              }}
            />
            <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '15px' }}>shares</span>
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>
            {((shares / TOTAL_SHARES) * 100).toFixed(4)}% ownership of {TOTAL_SHARES.toLocaleString()} total shares
          </div>
        </div>
      )}

      {/* Year Selector */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '28px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {years.map(year => (
          <button
            key={year}
            onClick={() => setSelectedYear(year)}
            style={{
              padding: '12px 20px',
              background: selectedYear === year ? 'linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)' : 'rgba(255, 255, 255, 0.08)',
              border: selectedYear === year ? 'none' : '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '10px',
              color: selectedYear === year ? '#0f1419' : '#fff',
              fontSize: '15px',
              fontWeight: selectedYear === year ? 'bold' : 'normal',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {year}
            {year === 2026 && <span style={{ marginLeft: '4px' }}>⭐</span>}
          </button>
        ))}
      </div>

      {/* Selected Year Details */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '16px',
        padding: '28px',
        border: selectedYear === 2026 ? '2px solid rgba(255, 193, 7, 0.5)' : '1px solid rgba(255, 193, 7, 0.2)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h3 style={{ color: '#fff', fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
            {selectedYear}
            {selectedYear === 2026 && <span style={{ marginLeft: '12px', fontSize: '16px', color: '#ffc107' }}>⭐ Setup & Early Growth Year</span>}
          </h3>
          {'note' in selectedData && (
            <p style={{ color: '#ffc107', fontSize: '14px', fontStyle: 'italic' }}>
              {selectedData.note}
            </p>
          )}
        </div>

        {/* Key Metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', borderRadius: '12px', padding: '20px', textAlign: 'center', border: '1px solid rgba(255, 193, 7, 0.2)' }}>
            <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', fontWeight: '600', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gold Production</div>
            <div style={{ color: '#ffc107', fontSize: '26px', fontWeight: '700' }}>
              {selectedData.goldProductionKgPerMonth.toLocaleString()} kg/month
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', marginTop: '6px', fontWeight: '500' }}>
              {selectedData.isRampYear
                ? `${selectedData.goldProductionKgPerYear.toLocaleString()} kg in ${selectedYear} (${selectedData.monthsOperating} operating months)`
                : `${selectedData.goldProductionKgPerYear.toLocaleString()} kg/year`}
            </div>
            {selectedData.isRampYear && (
              <div style={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '11px', marginTop: '4px', fontStyle: 'italic' }}>
                {selectedData.goldProductionKgPerMonth.toLocaleString()} × {selectedData.monthsOperating} months — plant ramps mid-year
              </div>
            )}
            {selectedYear === 2026 && (
              <div style={{ color: '#60a5fa', fontSize: '11px', marginTop: '6px', fontWeight: 600, lineHeight: '1.4' }}>
                1-plant baseline. Total yield &amp; dividends scale linearly with active plant count — roadmap targets 10 plants by end 2026, 30 by end 2027.
              </div>
            )}
            {(() => {
              const plants = Math.max(1, Math.round(selectedData.goldProductionKgPerMonth / GOLD_KG_PER_MONTH_PER_PLANT));
              return (
                <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(0,0,0,0.25)', borderRadius: '8px', textAlign: 'left' }}>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '11px', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>How we estimate this</div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '11.5px', lineHeight: '1.55', fontFamily: 'monospace' }}>
                    <div>Plant capacity: {CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH} tonnes/hour</div>
                    <div>× Effective hours: {CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY} hrs/day</div>
                    <div>× Operating days: {CALCULATION_CONSTANTS.OPERATING_DAYS_PER_MONTH} days/month</div>
                    <div>× Grade: {CALCULATION_CONSTANTS.DEFAULT_IN_SITU_GRADE_G_PER_TONNE} g gold per tonne</div>
                    <div>÷ 1,000 (grams → kilograms)</div>
                    <div>= {GOLD_KG_PER_MONTH_PER_PLANT.toLocaleString()} kg/month per plant</div>
                    {plants > 1 && (
                      <div>× {plants.toLocaleString()} plants</div>
                    )}
                    <div style={{ color: '#ffc107', fontWeight: 700, marginTop: '4px' }}>
                      = {selectedData.goldProductionKgPerMonth.toLocaleString()} kg/month
                    </div>
                    {selectedData.isRampYear ? (
                      <>
                        <div style={{ marginTop: '4px' }}>× {selectedData.monthsOperating} operating months</div>
                        <div style={{ color: '#ffc107', fontWeight: 700 }}>
                          = {selectedData.goldProductionKgPerYear.toLocaleString()} kg in {selectedYear}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ marginTop: '4px' }}>× 12 months</div>
                        <div style={{ color: '#ffc107', fontWeight: 700 }}>
                          = {selectedData.goldProductionKgPerYear.toLocaleString()} kg/year
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '10.5px', marginTop: '8px', lineHeight: '1.4', fontStyle: 'italic' }}>
                    Projection only — actual yield depends on ore grade, plant uptime &amp; recovery rate. Not a guarantee.
                  </div>
                </div>
              );
            })()}
          </div>

          <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)', borderRadius: '12px', padding: '20px', textAlign: 'center', border: '1px solid rgba(255, 193, 7, 0.2)' }}>
            <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', fontWeight: '600', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Estimated Dividend <span style={{ color: 'rgba(255,255,255,0.45)', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(projection)</span>
            </div>
            <div style={{ color: '#ffc107', fontSize: '26px', fontWeight: '700' }}>
              {figureReady(selectedYear) ? `~$${selectedData.estimatedDividendPerShare}/share` : '—'}
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', marginTop: '6px', fontWeight: '500' }}>per year (estimated)</div>
            {(() => {
              const grossRevenue = selectedData.goldProductionKgPerYear * CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG;
              const netMarginPct = 100 - CALCULATION_CONSTANTS.REFINING_PERCENT - CALCULATION_CONSTANTS.OPEX_PERCENT;
              const profit = grossRevenue * (netMarginPct / 100);
              if (!figureReady(selectedYear)) {
                return (
                  <div style={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '11px', marginTop: '6px', fontStyle: 'italic' }}>
                    Waiting for the live share count.
                  </div>
                );
              }
              const divisorLine = selectedYear === 2026
                ? `÷ ${Math.floor(liveSharesSold).toLocaleString()} shares sold so far`
                : selectedYear === 2027
                  ? 'January and April: 1 plant ÷ shares sold so far. July: 10 plants ÷ 1,400,000. October: 20 plants ÷ 1,400,000.'
                  : selectedYear === 2028
                    ? '÷ 1,400,000 shares. January and April use 20 plants. July and October use 30 plants.'
                    : '÷ 1,400,000 shares';
              return (
                <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(0,0,0,0.25)', borderRadius: '8px', textAlign: 'left' }}>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '11px', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>How we estimate this</div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '11.5px', lineHeight: '1.55', fontFamily: 'monospace' }}>
                    <div>Gold sold: {selectedData.goldProductionKgPerYear.toLocaleString()} kg</div>
                    <div>× Price: ${CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG.toLocaleString()}/kg</div>
                    <div>= Revenue: ${grossRevenue.toLocaleString()}</div>
                    <div>× {netMarginPct}% net margin (after refining &amp; opex)</div>
                    <div>= Profit: ${profit.toLocaleString()}</div>
                    <div>{divisorLine}</div>
                    <div style={{ color: '#ffc107', fontWeight: 700, marginTop: '4px' }}>
                      ≈ ${selectedData.estimatedDividendPerShare}/share/year
                    </div>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '10.5px', marginTop: '8px', lineHeight: '1.4', fontStyle: 'italic' }}>
                    Projection only — actual amounts depend on mining output, gold price &amp; operating costs. Not a guarantee.
                  </div>
                </div>
              );
            })()}
          </div>

          <div style={{ background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 143, 0, 0.1) 100%)', borderRadius: '12px', padding: '20px', textAlign: 'center', border: '1px solid rgba(255, 193, 7, 0.4)' }}>
            <div style={{ color: '#ffc107', fontSize: '13px', fontWeight: '600', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Your Projected Annual Dividend
            </div>
            <div style={{ color: '#fff', fontSize: '28px', fontWeight: '800', textShadow: '0 2px 8px rgba(255, 193, 7, 0.4)' }}>
              {figureReady(selectedYear) ? `~$${(selectedData.estimatedDividendPerShare * shares).toLocaleString()}` : '—'}
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px', marginTop: '6px', fontWeight: '500' }}>
              estimate for {shares.toLocaleString()} shares
            </div>
            <div style={{ marginTop: '12px', padding: '10px', background: 'rgba(0,0,0,0.25)', borderRadius: '8px', textAlign: 'left' }}>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '11px', fontWeight: 700, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>How we estimate this</div>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '11.5px', lineHeight: '1.55', fontFamily: 'monospace' }}>
                <div>{figureReady(selectedYear) ? `$${selectedData.estimatedDividendPerShare}` : '—'} per share (estimated)</div>
                <div>× {shares.toLocaleString()} shares (your holding)</div>
                <div style={{ color: '#ffc107', fontWeight: 700, marginTop: '4px' }}>
                  {figureReady(selectedYear) ? `≈ $${(selectedData.estimatedDividendPerShare * shares).toLocaleString()} for the year` : 'Waiting for the live share count'}
                </div>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '10.5px', marginTop: '8px', lineHeight: '1.4', fontStyle: 'italic' }}>
                Projection only — not a guaranteed payout. Final dividend depends on actual gold production &amp; sales.
              </div>
            </div>
          </div>
        </div>

        {/* Payout Schedule */}
        <div>
          <h4 style={{ color: '#ffc107', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
            💰 Payout Schedule
          </h4>

          {/* 2026–2027 growth years notice */}
          {selectedYear === 2026 && (
            <div style={{
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#f59e0b'
            }}>
              ⚠️ <strong>Growth Years 2026–2027:</strong> 2026 and 2027 are designated growth years focused on scaling production capacity from our initial wash plants toward the broader 200+ plant roadmap. Annual dividend projections shown below reflect this scaling phase, with full distribution figures subject to actual production and sales.
            </div>
          )}

          {/* Shares-sold split. January 2027 and April 2027 are the first payouts shown. */}
          {(selectedYear === 2026 || selectedYear === 2027) && (
            <div style={{
              backgroundColor: 'rgba(251, 191, 36, 0.08)',
              border: '1px solid rgba(251, 191, 36, 0.35)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#fbbf24',
              lineHeight: '1.6'
            }}>
              📣 <strong>Early-distribution window:</strong> {DYNAMIC_DENOMINATOR_NOTICE}
            </div>
          )}

          {/* Scaling & expansion notice (always shown so investors see the
              single-plant baseline vs the multi-plant scaling roadmap) */}
          <div style={{
            backgroundColor: 'rgba(59, 130, 246, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.35)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#bfdbfe',
            lineHeight: '1.6'
          }}>
            <div style={{ color: '#60a5fa', fontWeight: 600, marginBottom: '6px' }}>🏗️ Scaling &amp; Expansion</div>
            <p style={{ margin: '0 0 6px 0' }}><strong style={{ color: '#fff' }}>Baseline:</strong> {SCALING_NOTICE.baseline}</p>
            <p style={{ margin: '0 0 6px 0' }}><strong style={{ color: '#fff' }}>Scaling potential:</strong> {SCALING_NOTICE.scalingPotential}</p>
            <p style={{ margin: 0 }}><strong style={{ color: '#fff' }}>Operational lead time ({PLANT_ENGINEERING_LEAD_TIME_DAYS} days/plant):</strong> {SCALING_NOTICE.leadTime}</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px' }}>
            {(selectedData.quarterlyPayouts ?? []).length === 0 && selectedYear === 2026 && (
              <div style={{
                gridColumn: '1 / -1',
                backgroundColor: 'rgba(255, 193, 7, 0.08)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '12px',
                padding: '16px',
                color: '#fbbf24',
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                No public dividend payout is shown for 2026. The first payouts shown are January 2027 and April 2027.
              </div>
            )}
            {(selectedData.quarterlyPayouts ?? []).map((payout: any, index: any) => {
              const totalAmount = (payout.amount * shares).toLocaleString();
              const isFirstPayout = selectedYear === 2027 && payout.month === 'January';
              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: isFirstPayout ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255, 193, 7, 0.08)',
                    border: isFirstPayout ? '2px solid rgba(16, 185, 129, 0.5)' : '1px solid rgba(255, 193, 7, 0.3)',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center',
                    position: 'relative' as const
                  }}
                >
                  {isFirstPayout && (
                    <div style={{
                      position: 'absolute',
                      top: '-8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: '#10b981',
                      color: '#fff',
                      fontSize: '13px',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontWeight: '600'
                    }}>
                      FIRST PAYOUT
                    </div>
                  )}
                  <div style={{ color: '#ffc107', fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{payout.month}</div>
                  <div style={{ color: '#fff', fontSize: '22px', fontWeight: '700' }}>
                    {figureReady(selectedYear) ? `$${totalAmount}` : '—'} {payout.note}
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', marginTop: '6px', fontWeight: '500' }}>total payout</div>
                </div>
              );
            })}
            {(selectedData.semiAnnualPayouts ?? []).map((payout, index) => {
              const totalAmount = (payout.amount * shares).toLocaleString();
              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: 'rgba(255, 193, 7, 0.08)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ color: '#ffc107', fontSize: '13px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{payout.month}</div>
                  <div style={{ color: '#fff', fontSize: '22px', fontWeight: '700' }}>
                    {figureReady(selectedYear) ? `$${totalAmount}` : '—'} {payout.note}
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', marginTop: '6px', fontWeight: '500' }}>total payout</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Per Share Breakdown */}
        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(255, 193, 7, 0.2)' }}>
          <h4 style={{ color: '#ffc107', fontSize: '18px', fontWeight: '600', marginBottom: '16px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>
            📊 Per Share Breakdown (Base Values)
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
            {/* Estimated Dividend Per Share */}
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center'
              }}
            >
              <div style={{ color: '#ffc107', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Annual Dividend</div>
              <div style={{ color: '#fff', fontSize: '22px', fontWeight: '700' }}>
                {figureReady(selectedYear) ? `$${selectedData.estimatedDividendPerShare.toLocaleString()}` : '—'}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', marginTop: '6px', fontWeight: '500' }}>per share/year</div>
            </div>

            {/* Payout Per Share */}
            {(selectedData.quarterlyPayouts ?? []).map((payout: any, index: any) => (
              <div
                key={index}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 193, 7, 0.2)',
                  borderRadius: '12px',
                  padding: '16px',
                  textAlign: 'center'
                }}
              >
                <div style={{ color: '#ffc107', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{payout.month}</div>
                <div style={{ color: '#fff', fontSize: '20px', fontWeight: '700' }}>
                  ${payout.amount.toLocaleString()} {payout.note}
                </div>
                <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', marginTop: '6px', fontWeight: '500' }}>per share</div>
              </div>
            ))}
            {(selectedData.semiAnnualPayouts ?? []).map((payout, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 193, 7, 0.2)',
                  borderRadius: '12px',
                  padding: '16px',
                  textAlign: 'center'
                }}
              >
                <div style={{ color: '#ffc107', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{payout.month}</div>
                <div style={{ color: '#fff', fontSize: '20px', fontWeight: '700' }}>
                  ${payout.amount.toLocaleString()} {payout.note}
                </div>
                <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', marginTop: '6px', fontWeight: '500' }}>per share</div>
              </div>
            ))}

            {/* Gold Production */}
            <div
              style={{
                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center'
              }}
            >
              <div style={{ color: '#ffc107', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gold Output</div>
              <div style={{ color: '#fff', fontSize: '22px', fontWeight: '700' }}>
                {selectedData.goldProductionKgPerYear.toLocaleString()} kg
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', marginTop: '6px', fontWeight: '500' }}>
                {selectedData.isRampYear ? `in ${selectedYear} (${selectedData.monthsOperating} operating months)` : 'per year'}
              </div>
            </div>
          </div>
          <div style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', marginTop: '16px', textAlign: 'center', fontStyle: 'italic', backgroundColor: 'rgba(255, 255, 255, 0.03)', padding: '10px', borderRadius: '8px' }}>
            These are the base values for 1 share. Your totals above are calculated by multiplying these values by your {shares.toLocaleString()} shares.
          </div>
        </div>

        {/* Calculation Formula Breakdown */}
        <div style={{ marginTop: '24px', padding: '16px', backgroundColor: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '12px' }}>
          <h4 style={{ color: '#fbbf24', fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}>📐 How these figures are calculated:</h4>
          <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#d1d5db' }}>
            <p style={{ margin: '0 0 2px 0' }}>Gold production: <span style={{ color: '#fff' }}>{selectedData.goldProductionKgPerYear.toLocaleString()} kg{selectedData.isRampYear ? ` in ${selectedYear} (${selectedData.goldProductionKgPerMonth} kg/month × ${selectedData.monthsOperating} operating months)` : '/year'}</span></p>
            {(() => {
              const plantsActive = Math.max(1, Math.round(selectedData.goldProductionKgPerMonth / GOLD_KG_PER_MONTH_PER_PLANT));
              return (
                <p style={{ margin: '0 0 2px 0', paddingLeft: '14px', color: 'rgba(255,255,255,0.75)', fontSize: '12px' }}>
                  Per-plant baseline:{' '}
                  <span style={{ color: '#fff' }}>
                    {CALCULATION_CONSTANTS.PLANT_CAPACITY_TPH} TPH × {CALCULATION_CONSTANTS.EFFECTIVE_HOURS_PER_DAY} hrs/day × {CALCULATION_CONSTANTS.OPERATING_DAYS_PER_MONTH} days/month × {CALCULATION_CONSTANTS.DEFAULT_IN_SITU_GRADE_G_PER_TONNE} g/tonne ÷ 1,000
                  </span>{' '}
                  = <span style={{ color: '#fbbf24', fontWeight: 600 }}>{GOLD_KG_PER_MONTH_PER_PLANT.toFixed(2)} kg/month per 100 TPH plant</span>
                  {plantsActive > 1 && (
                    <> · × <span style={{ color: '#fff' }}>{plantsActive} active plants</span> = <span style={{ color: '#fbbf24', fontWeight: 600 }}>{selectedData.goldProductionKgPerMonth.toLocaleString()} kg/month</span></>
                  )}
                </p>
              );
            })()}
            <p style={{ margin: '0 0 2px 0' }}>Gold price: <span style={{ color: '#fff' }}>${CALCULATION_CONSTANTS.GOLD_PRICE_USD_PER_KG.toLocaleString()}/kg</span></p>
            <p style={{ margin: '0 0 2px 0' }}>Net margin (after {CALCULATION_CONSTANTS.REFINING_PERCENT}% refining + {CALCULATION_CONSTANTS.OPEX_PERCENT}% opex): <span style={{ color: '#fff' }}>50%</span></p>
            <p style={{ margin: '0 0 2px 0' }}>
              Shares used in calculation:{' '}
              <span style={{ color: '#10b981', fontWeight: 600 }}>
                {selectedYear === 2026
                  ? (figureReady(selectedYear) ? `${Math.floor(liveSharesSold).toLocaleString()} shares sold so far` : 'Waiting for the live share count')
                  : selectedYear === 2027
                    ? 'January and April use shares sold so far. July and October use 1,400,000.'
                    : selectedYear === 2028
                      ? '1,400,000 shares'
                      : '1,400,000 shares'}
              </span>
            </p>
            <p style={{ margin: '0 0 2px 0' }}>Annual dividend/share: <span style={{ color: '#10b981', fontWeight: '600' }}>{figureReady(selectedYear) ? `$${selectedData.estimatedDividendPerShare}` : '—'}</span></p>
            <p style={{ margin: '8px 0 0 0', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>
              Your shares: <span style={{ color: '#fff' }}>{shares.toLocaleString()}</span> × {figureReady(selectedYear) ? `$${selectedData.estimatedDividendPerShare}` : '—'} = <span style={{ color: '#f59e0b', fontWeight: '700' }}>{figureReady(selectedYear) ? `$${(selectedData.estimatedDividendPerShare * shares).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—'}</span>/year
            </p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div style={{
        marginTop: '24px',
        padding: '18px',
        backgroundColor: 'rgba(255, 193, 7, 0.08)',
        border: '1px solid rgba(255, 193, 7, 0.3)',
        borderRadius: '12px',
        fontSize: '13px',
        color: '#ffc107',
        textAlign: 'center'
      }}>
        <strong>⚠️ Important Disclaimer:</strong> <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{DIVIDEND_DISCLAIMER}</span>
      </div>
    </div>
  );
};

